/**
 * 261029 P3 (F1/F2) — CHỖ GIAO một lần cho lượt đọc bắn SỚM (trước khi component cần dữ liệu mount).
 * Khác `workflowReadRequests.ts` (chỉ gộp request ĐANG BAY, xong là quên): ở đây kết quả của lượt đọc sớm được giữ NGẮN (≤ HANDOFF_TTL_MS) để component mount sau lấy dùng thay vì gọi lại.
 *
 * Luật (bất biến «dữ liệu hiển thị sau ghi/refresh luôn là kết quả gọi mới»):
 *  - Mỗi lượt gắn `writeEpoch` (số lệnh ghi đã xong của tab) + `generation` (tăng khi đổi plan / làm mới toàn trang) lúc BẮT ĐẦU. Kiểm cả lúc LẤY lẫn lúc RESPONSE VỀ:
 *    đã có ghi xong / đổi generation / quá TTL ⇒ bỏ kết quả, gọi `fetch()` mới. Lượt đọc bắt đầu trước một lần ghi KHÔNG BAO GIỜ được giao, kể cả khi response về muộn.
 *  - Promise bị reject không được giữ; người lấy gặp lỗi của lượt sớm thì gọi `fetch()` mới (lỗi hiển thị là lỗi của lượt gọi thật).
 *  - Mỗi người lấy nhận BẢN SAO riêng. `takes` = số người lấy tối đa (mặc định 1 = consume-once; khoá có hai consumer đã biết dùng 2 để cả hai cùng nhận MỘT phiên bản). Lấy hết ngân sách ⇒ xoá.
 *  - Refresh / poll / lượt gọi sau thao tác ghi KHÔNG đi qua `take…` (chỉ lần nạp ĐẦU của component mới đi qua) nên luôn gọi mới.
 */
import { retireWorkflowReads, workflowReadGeneration, workflowWriteEpoch } from './workflowReadRequests';

export const HANDOFF_TTL_MS = 10_000;
interface Entry { promise: Promise<unknown>; epoch: number; generation: number; at: number; takes: number }
const store = new Map<string, Entry>();
// generation dùng CHUNG với bộ gộp request (`workflowReadRequests.ts`): reset vừa xoá kho vừa «cho nghỉ hưu» các GET đang bay ⇒ lượt đọc mới sau reset là request MỚI (Reviewer F2).
const currentGeneration = (): number => workflowReadGeneration();

const usable = (e: Entry): boolean => e.epoch === workflowWriteEpoch() && e.generation === currentGeneration() && Date.now() - e.at <= HANDOFF_TTL_MS;
const clone = <T,>(v: T): T => (typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

/** Đổi plan / làm mới toàn trang: bỏ mọi lượt đọc sớm đang giữ VÀ làm vô hiệu lượt còn đang bay. */
export function resetWorkflowHandoff(): void { retireWorkflowReads(); store.clear(); }
export const workflowHandoffSize = (): number => store.size;
export const workflowHandoffGeneration = (): number => currentGeneration();

/** Bắn lượt đọc sớm cho `key` (idempotent: đã có lượt còn hợp lệ ⇒ không bắn thêm). Không ném lỗi. */
export function startWorkflowHandoff<T>(key: string, fetch: () => Promise<T>, takes = 1): void {
  const live = store.get(key);
  if (live && usable(live)) return;
  const promise = Promise.resolve().then(fetch);
  const entry: Entry = { promise, epoch: workflowWriteEpoch(), generation: currentGeneration(), at: Date.now(), takes };
  store.set(key, entry);
  promise.then(() => undefined, () => { if (store.get(key) === entry) store.delete(key); });
}

/** Chờ kết quả của lượt đọc sớm đang giữ mà KHÔNG lấy phần của ai (để nối chuỗi đọc sớm tiếp theo). `null` nếu không có lượt hợp lệ / lượt lỗi. */
export function peekWorkflowHandoff<T>(key: string): Promise<T | null> {
  const entry = store.get(key);
  if (!entry || !usable(entry)) return Promise.resolve(null);
  return (entry.promise as Promise<T>).then(v => (entry.epoch === workflowWriteEpoch() && entry.generation === currentGeneration() ? clone(v) : null), () => null);
}

/** Lấy kết quả lượt đọc sớm của `key` nếu còn hợp lệ, ngược lại `fetch()` mới. Chỉ dùng cho lần nạp ĐẦU của component. */
export function takeWorkflowHandoff<T>(key: string, fetch: () => Promise<T>): Promise<T> {
  const entry = store.get(key);
  if (!entry) return fetch();
  if (!usable(entry)) { store.delete(key); return fetch(); }
  entry.takes -= 1;
  if (entry.takes <= 0 && store.get(key) === entry) store.delete(key);
  return (entry.promise as Promise<T>).then(
    v => (entry.epoch === workflowWriteEpoch() && entry.generation === currentGeneration() && Date.now() - entry.at <= HANDOFF_TTL_MS ? clone(v) : fetch()),
    () => fetch(),
  );
}

/** Kiểu `Promise` LƯỜI: chỉ quyết định lấy-hay-gọi-mới khi có người `await`/`.then` (lúc DÙNG, không phải lúc tạo) — cho `initialReads` của trang. */
export function lazyWorkflowHandoff<T>(key: string, fetch: () => Promise<T>): Promise<T> {
  const take = () => takeWorkflowHandoff(key, fetch);
  return { then: (a?: any, b?: any) => take().then(a, b), catch: (b?: any) => take().catch(b), finally: (f?: any) => take().finally(f), [Symbol.toStringTag]: 'Promise' } as Promise<T>;
}
